import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InstanceApi from "../../api/instance/InstanceApi";
import './style.scss'

// Context.
import { InstanceContext } from '../../context/InstanceContext';
import { HostContext } from '../../context/HostContext';

// Components.
import StatusIcon from '../../component/StatusIcon';
import ConditionalRender from "../../helpers/ConditionalRender";
import { hostMetaData, instanceMetaData, instanceTabs } from "../Constants";

// Types
import InstanceTarget from "../../types/instance/InstanceTarget";
import HostTarget from "../../types/host/HostTarget";

// Components
import TagsList from "../TagsList";
import InstanceAgBadge from "../cluster/InstanceAgBadge";
import { Dropdown } from "../../components/forms";

function HeaderFilter(props: {
    instanceId: number,
    hostId: number,
    heading: string,
    filterItems?: (item: InstanceTarget | HostTarget) => void,
    type?: string
}) {
    const { instances } = useContext(InstanceContext);
    const { hosts } = useContext(HostContext);
    const [metaData, setMetaData] = useState<any>({});
    const navigate = useNavigate();
    // Change state type to Record<string, string>
    const [tags, setTags] = useState<Record<string, string>>({});
    const metaDataList = props.instanceId ? instanceMetaData : hostMetaData

    const hostsToShow: HostTarget[] = useMemo(() => {
        return JSON.parse(JSON.stringify(hosts))
    }, [hosts])

    const instancesToShow: InstanceTarget[] = useMemo(() => {
        return JSON.parse(JSON.stringify(props.filterItems ? instances.filter(props.filterItems) : instances))
    }, [instances, props.filterItems])

    useEffect(() => {
        // Reset tags when instanceId changes
        setTags({});
        if (props.instanceId) {
            // Fetch tags and set the Record<string, string> state
            InstanceApi.getTags(props.instanceId).then(fetchedTags => {
                setTags(fetchedTags);
            });
        }
    }, [props.instanceId]);

    useEffect(() => {
        if (props.instanceId) {
            let getSelectedInstanceData = instances.find(instance => instance.id === props.instanceId) || {}
            setMetaData(getSelectedInstanceData)
        }
    }, [props.instanceId]);

    useEffect(() => {
        if (props.hostId) {
            let getSelectedHost = hosts.find(host => host.id === props.hostId) || {}
            setMetaData(getSelectedHost)
        }
    }, [props.hostId]);

    const isInstances = props.type === 'instances';
    const pickerItems = (isInstances ? instancesToShow : hostsToShow)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(item => ({ value: item.id, label: item.name }));
    const selectedId = isInstances ? props.instanceId : props.hostId;

    const goTo = (id: number) => {
        if (!id || id === selectedId) return;
        navigate(isInstances ? `/instances/${id}/activity` : `/hosts/${id}`);
    };

    return (
            <>
                <div className="dropdown d-inline">
                    {(props.instanceId > 0 || props.hostId > 0) && (
                            <StatusIcon instanceId={props.instanceId} hostId={props.hostId} mode="public"/>
                    )}
                    <Dropdown
                        id="filter"
                        value={selectedId > 0 ? selectedId : ''}
                        onChange={(value) => goTo(Number(value))}
                        options={pickerItems}
                        placeholder={props.heading}
                        filter
                        filterPlaceholder={`Search ${props.type}`}
                        ariaLabel={`Select ${props.type === 'hosts' ? 'host' : 'instance'}`}
                        className="dbm-dropdown-heading"
                    />

                </div>
                <ConditionalRender if={Object.keys(metaData).some((key) => metaDataList.includes(key))}>
                    <div className="dropdown d-inline">

                        <button type="button" id="info" className="btn info-toggle" data-bs-toggle="dropdown"
                                aria-expanded="false">
                            <i className="fa fa-info-circle" aria-hidden="true"/>
                        </button>

                        <div id="info-menu" className="row g-0 dropdown-menu dropdown-menu-scroll" aria-labelledby="info"
                             onClick={(e) => e.stopPropagation()}>
                            {Object.entries(metaData)
                                    .filter(([key]) => metaDataList.includes(key))

                                .map(([key, value]) =>{
                                    const formattedValue = value ? JSON.stringify(value).replaceAll('"', '') : 'N/A';
                                    const isValueTrue = value === true;
                                    const isKeyInInstanceTabs = instanceTabs.includes(key);
                                    const isKeyNotCollected = isValueTrue && isKeyInInstanceTabs && !metaData[`${key.replace('has', 'collect')}`];
                                    return (
                                            <div key={key}>
                                                    <span>{key}</span>
                                                    <span>{formattedValue} {' '}{ isKeyNotCollected ? '(not collected)' : ''}</span>
                                                </div>
                                            );
                            })}
                        </div>
                    </div>
                </ConditionalRender>

                <InstanceAgBadge instanceId={props.instanceId}/>

                {/* Transform tags back to Tags[] before passing to TagsList */}
                <ConditionalRender if={Object.keys(tags).length > 0}>
                    <TagsList tagList={Object.entries(tags).map(([name, value]) => ({
                        tagname: name,
                        tagvalue: value,
                        datasourceid: props.instanceId // Add datasourceid if needed by Tags type, though TagsList doesn't use it
                    }))} />
                </ConditionalRender>
                <br/>
            </>
    );
}

export default HeaderFilter;
